import { createFileRoute } from '@tanstack/react-router';
import { Trophy } from 'lucide-react';
import { Sidebar } from '@/components/layout/sidebar';
import { useLeaderboard, type LeaderboardRow } from '@/features/leaderboard/queries';
import { getInitials } from '@/lib/format';

export const Route = createFileRoute('/_auth/leaderboard')({ component: LeaderboardPage });

function LeaderboardPage(): JSX.Element {
  const { data: rows, isLoading } = useLeaderboard();

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="border-b border-border/[0.08] pl-14 pr-6 md:px-6 py-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
            Team performance
          </div>
          <h1 className="mt-1 font-display text-2xl text-text">Leaderboard</h1>
          <p className="mt-1 text-sm text-text-dim">
            Calls, demos, and closes — ranked by today's dial volume.
          </p>
        </div>

        {isLoading ? (
          <div className="p-6 text-text-mute">Loading…</div>
        ) : (
          <div className="px-6 py-5">
            <div className="overflow-hidden rounded-xl border border-border/[0.08] bg-surface-2">
              <div className="grid grid-cols-[60px_1.4fr_repeat(5,_1fr)] gap-3 border-b border-border/[0.08] px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-mute">
                <div>#</div>
                <div>Agent</div>
                <div className="text-right">Dials</div>
                <div className="text-right">Connects</div>
                <div className="text-right">Demos T</div>
                <div className="text-right">Demos W</div>
                <div className="text-right">Closes M</div>
              </div>

              {(rows ?? []).length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-text-mute">
                  <Trophy className="h-5 w-5" />
                  <span className="text-sm">No active agents yet.</span>
                </div>
              ) : (
                (rows ?? []).map((r, i) => <LeaderboardRowItem key={r.agent_id ?? `row-${i}`} row={r} index={i} />)
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function LeaderboardRowItem({ row, index }: { row: LeaderboardRow; index: number }) {
  const isFirst = index === 0;
  return (
    <div
      className={`grid grid-cols-[60px_1.4fr_repeat(5,_1fr)] items-center gap-3 border-b border-border/[0.08] px-4 py-3 text-sm transition-all duration-fast last:border-b-0 hover:-translate-y-px hover:border-border-hi hover:bg-surface-hi hover:shadow-[0_4px_16px_-8px_rgb(var(--accent)/0.25)] ${
        isFirst ? 'bg-accent/[0.04]' : ''
      }`}
    >
      <div>
        <span
          className={`flex h-7 w-7 items-center justify-center rounded-full font-mono text-[11px] ${
            isFirst
              ? 'bg-gradient-to-br from-accent to-accent/60 font-bold text-accent-fg'
              : 'bg-surface text-text-dim'
          }`}
        >
          {index + 1}
        </span>
      </div>
      <div className="flex items-center gap-3 min-w-0">
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent/15 text-[10px] font-semibold text-accent">
          {getInitials(row.full_name)}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-text">{row.full_name ?? '—'}</div>
          <div className="text-[10.5px] uppercase tracking-wider text-text-mute">Agent</div>
        </div>
      </div>
      <div className="text-right font-mono text-text">{row.dials_today ?? 0}</div>
      <div className="text-right font-mono text-text">{row.connects_today ?? 0}</div>
      <div className="text-right font-mono text-text">{row.demos_today ?? 0}</div>
      <div className="text-right font-mono text-text">{row.demos_week ?? 0}</div>
      <div className="text-right font-mono text-text">{row.closes_month ?? 0}</div>
    </div>
  );
}
